/**
 * PermittingWorkspace — the Permitting workspace: Zones & Jurisdictions (add/edit the setback/
 * height/coverage code library that feeds the pre-design report) and a cross-project Permits
 * board, as tabs of one top-level workspace (2026-09, Matt's call — see docs/PERMITTING_WORKSPACE.md).
 *
 * Mirrors StudioWorkspace.jsx's shell exactly: docks into the content area beside SiteNav
 * (tray-aware via `.aj-permitting-ws` in outline.css), `ws=permitting` + `wsTool` for the active
 * tab, sessionStorage/localStorage persistence, `open-permitting`/`close-permitting` events.
 *
 * Gated on the `permitting` module (manager + admin) — see SiteNav.jsx's `canPermitting`.
 */
import { useEffect, useState } from 'react'
import { isEmbedded } from '../outline/state.js'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import PermitZonesTab from './PermitZonesTab.jsx'
import PermitsBoardTab from './PermitsBoardTab.jsx'
import PlanCheckPerformanceTab from './PlanCheckPerformanceTab.jsx'

const STORE_KEY = 'aj:permitting:ws'
const TABS = ['zones', 'board', 'performance']
const TAB_LABEL = { zones: 'Zones & Jurisdictions', board: 'Permits', performance: 'Plan Check Performance' }

function readStored() {
  try {
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    if (ws === 'permitting') return { active: true, tool: params.get('wsTool') || 'zones' }
    if (ws !== null) return null
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch { return null }
}

export default function PermittingWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [tool, setTool] = useState((stored && stored.tool) || 'zones')
  // `open-permitting` may name a jurisdiction to land on (the Parcel Research tool's
  // "Edit in Permitting" links). A fresh object each time so the same id re-opens it.
  const [focus, setFocus] = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      if (d.tool) setTool(d.tool)
      if (d.jurisdictionId) setFocus({ jurisdictionId: d.jurisdictionId })
      setActive(true)
    }
    function onClose() { setActive(false) }
    window.addEventListener('open-permitting', onOpen)
    window.addEventListener('close-permitting', onClose)
    return () => { window.removeEventListener('open-permitting', onOpen); window.removeEventListener('close-permitting', onClose) }
  }, [])

  useEffect(() => {
    try {
      if (active) {
        const json = JSON.stringify({ active: true, tool })
        sessionStorage.setItem(STORE_KEY, json); localStorage.setItem(STORE_KEY, json)
      } else {
        sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY)
      }
    } catch { /* ignore */ }
  }, [active, tool])

  useEffect(() => {
    const url = new URL(window.location)
    if (active) {
      url.searchParams.set('ws', 'permitting'); url.searchParams.set('wsTool', tool)
      document.title = 'Permitting Hub — Alder James'
    } else if (url.searchParams.get('ws') === 'permitting') {
      url.searchParams.delete('ws'); url.searchParams.delete('wsTool')
    }
    window.history.replaceState(null, '', url)
  }, [active, tool])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  if (!active) return null

  return (
    <div className="aj-permitting-ws" style={S.panel}>
      <WorkspaceHeader
        title="Permitting Hub"
        tabs={(
          <div style={workspaceTabsWrapStyle}>
            {TABS.map(t => (
              <button key={t} style={workspaceTabStyle(tool === t)} onClick={() => setTool(t)}>{TAB_LABEL[t]}</button>
            ))}
          </div>
        )}
      />
      <div style={S.content}>
        {tool === 'zones' && <PermitZonesTab focus={focus} />}
        {tool === 'board' && <PermitsBoardTab />}
        {tool === 'performance' && <PlanCheckPerformanceTab />}
      </div>
    </div>
  )
}

const S = {
  panel: { display: 'flex', flexDirection: 'column', height: '100%' },
  content: { flex: 1, overflow: 'hidden', background: 'var(--aj-bg-page)' },
}
